import { computed } from 'vue'
import { useGetters } from 'vuex-composition-helpers'
import { useDefaultFieldValues } from './useDefaultFieldValues'
import {
  BusinessUnitLookup,
  CompanyLookup,
  LocationLookup,
  LookupEntity,
  UserLookup,
} from '@ironsight/form-builder'
import { sortBy } from 'lodash'

interface LookupField {
  lookupEntity: {
    entity: string
  }
  defaultValue?: string | number | null
  uniqueKey: () => string
}

interface LookupChoice {
  name: string
  value: string
  build: () => LookupEntity
}

export function useDefaultLookupFieldValues(field: LookupField) {
  const defaultValueComposable = useDefaultFieldValues(field)

  const entityType = computed(() => field.lookupEntity.entity)

  const lookupTypeChoices = computed((): LookupChoice[] => [
    {
      name: 'User',
      value: 'user',
      build: () => new UserLookup(),
    },
    {
      name: 'Company',
      value: 'company',
      build: () => new CompanyLookup(),
    },
    {
      name: 'Location',
      value: 'location',
      build: () => new LocationLookup(),
    },
    {
      name: 'Business Unit',
      value: 'business_unit',
      build: () => new BusinessUnitLookup(),
    },
  ])

  function getLookupBuild(lookupChoiceValue: string) {
    const choice = lookupTypeChoices.value.find((c) => c.value === lookupChoiceValue)
    return choice?.build()
  }

  function getCurrentLookupTypeName(entityValue: string): string {
    const choice = lookupTypeChoices.value.find((choice) => choice.value === entityValue)
    return choice?.name || 'value'
  }

  const { getVisibleUsers } = useGetters('users', ['getVisibleUsers'])
  const { getAllCompanies } = useGetters('companies', ['getAllCompanies'])
  const { getAllBusinessUnits } = useGetters('businessUnits', ['getAllBusinessUnits'])

  const userOptions = computed(() => {
    const users = Array.isArray(getVisibleUsers.value)
      ? getVisibleUsers.value
      : Object.values(getVisibleUsers.value || {})

    return sortBy(users, ['name'])
  })

  const companyOptions = computed(() => {
    const companies = getAllCompanies.value
    return Array.isArray(companies) ? companies : Object.values(companies || {})
  })

  const businessUnitOptions = computed(() => {
    const businessUnits = getAllBusinessUnits.value
    return Array.isArray(businessUnits) ? businessUnits : Object.values(businessUnits || {})
  })

  const currentOptions = computed(() => {
    switch (entityType.value) {
      case 'user':
        return userOptions.value
      case 'company':
        return companyOptions.value
      case 'business_unit':
        return businessUnitOptions.value
      default:
        return []
    }
  })

  const isLocationEntity = computed(() => entityType.value === 'location')

  const currentDisplayProps = computed(() => {
    switch (entityType.value) {
      case 'user':
        return { textKey: 'name', valueKey: 'id' }
      case 'company':
        return { textKey: 'name', valueKey: 'id' }
      case 'business_unit':
        return { textKey: 'name', valueKey: 'id' }
      default:
        return { textKey: 'name', valueKey: 'id' }
    }
  })

  const currentDataType = computed(() => {
    switch (entityType.value) {
      case 'user':
      case 'business_unit':
        return 'string'
      case 'location':
      case 'company':
        return 'number'
      default:
        return 'string'
    }
  })

  const currentLookupTypeName = computed(() => {
    return getCurrentLookupTypeName(entityType.value)
  })

  const locationValue = computed(() => {
    return entityType.value === 'location' ? (field.defaultValue as number) || undefined : undefined
  })

  function onDefaultValueChange(value: string | number | number[] | null | undefined) {
    if (value === null || value === undefined) {
      field.defaultValue = null
      return
    }
    if (Array.isArray(value)) {
      field.defaultValue = value.length > 0 ? value[0] : null
      return
    }
    if (currentDataType.value === 'number') {
      field.defaultValue = typeof value === 'string' ? parseInt(value, 10) : value
    } else {
      field.defaultValue = value
    }
  }

  function changeLookupType(
    lookupChoiceValue: string,
    emitChanges: (propertyKey: string, value: any) => void,
  ) {
    const lookupEntity = getLookupBuild(lookupChoiceValue)
    if (lookupEntity) {
      field.defaultValue = null
      // Update local field immediately for reactivity
      field.lookupEntity = { ...lookupEntity }
      emitChanges('lookupEntity', lookupEntity)
    }
  }

  return {
    ...defaultValueComposable,
    lookupTypeChoices,
    currentOptions,
    currentDisplayProps,
    currentDataType,
    currentLookupTypeName,
    isLocationEntity,
    locationValue,
    onDefaultValueChange,
    changeLookupType,
  }
}
